import React from 'react';
import SummaryTabBase from '../Shared/SummaryTabBase';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';

interface Props {
  landUseChangeTotal: number | null;
  roadPavementTransportTotal: number | null;
  emissionsDevelopmentBuildingConstructionTotal: number | null;
  roadMaintenanceTotal: number | null;
  emissionsPurchasedElectricityTotal: number | null;
  totalConstructionOverriden: number | null;
  setTotalConstructionOverriden: React.Dispatch<React.SetStateAction<number | null>>;
  totalOperationOverriden: number | null;
  setTotalOperationOverriden: React.Dispatch<React.SetStateAction<number | null>>;
}

const calculateTotal = (...subtotals: (number | null)[]) => {
  if (subtotals.every((subtotal) => subtotal === null)) {
    return null;
  }

  return subtotals.reduce((acc: number, subtotal) => acc + (subtotal || 0), 0);
};

const SummaryTab: React.FC<Props> = ({
  landUseChangeTotal,
  emissionsDevelopmentBuildingConstructionTotal,
  roadPavementTransportTotal,
  roadMaintenanceTotal,
  emissionsPurchasedElectricityTotal,
  totalConstructionOverriden,
  setTotalConstructionOverriden,
  totalOperationOverriden,
  setTotalOperationOverriden,
}) => {
  const anyConstruction = !!(
    landUseChangeTotal ||
    roadPavementTransportTotal ||
    emissionsDevelopmentBuildingConstructionTotal
  );

  const totalSubTotalConstruction = calculateTotal(
    landUseChangeTotal,
    roadPavementTransportTotal,
    emissionsDevelopmentBuildingConstructionTotal
  );

  const anyOperations = !!(roadMaintenanceTotal || emissionsPurchasedElectricityTotal);

  const totalSubTotalOperation = calculateTotal(
    roadMaintenanceTotal,
    emissionsPurchasedElectricityTotal
  );

  const constructionTableConfig = {
    colgroup: (
      <colgroup>
        <col style={{ width: '25%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '35%' }} />
        <col style={{ width: '20%' }} />
      </colgroup>
    ),
    headers: (
      <tr>
        <th>Land Use Change</th>
        <th>Road Pavement</th>
        <th>Emissions from Development and Building Construction</th>
        <th>Subtotal</th>
      </tr>
    ),
    row: (
      <tr>
        <td>{landUseChangeTotal && <NumberFormatterSinSeparador value={landUseChangeTotal} />}</td>
        <td>
          {roadPavementTransportTotal && (
            <NumberFormatterSinSeparador value={roadPavementTransportTotal} />
          )}
        </td>
        <td>
          {emissionsDevelopmentBuildingConstructionTotal && (
            <NumberFormatterSinSeparador value={emissionsDevelopmentBuildingConstructionTotal} />
          )}
        </td>
        <td className="summary-subtotal">
          {anyConstruction && totalSubTotalConstruction && (
            <>
              {' '}
              <NumberFormatterSinSeparador value={totalSubTotalConstruction} /> t CO<sub>2</sub>e{' '}
            </>
          )}
        </td>
      </tr>
    ),
  };

  const operationTableConfig = {
    colgroup: (
      <colgroup>
        <col style={{ width: '45%' }} />
        <col style={{ width: '35%' }} />
        <col style={{ width: '20%' }} />
      </colgroup>
    ),
    headers: (
      <tr>
        <th>Emissions from Road Maintenance Activities</th>
        <th>Emissions from Purchased Electricity</th>
        <th>Subtotal</th>
      </tr>
    ),
    row: (
      <tr>
        <td>
          {roadMaintenanceTotal && (
            <>
              {' '}
              <NumberFormatterSinSeparador value={roadMaintenanceTotal} /> t CO<sub>2</sub>e{' '}
            </>
          )}
        </td>
        <td>
          {emissionsPurchasedElectricityTotal && (
            <>
              {' '}
              <NumberFormatterSinSeparador value={emissionsPurchasedElectricityTotal} /> t CO
              <sub>2</sub>e{' '}
            </>
          )}
        </td>
        <td className="summary-subtotal">
          {anyOperations && (
            <>
              {' '}
              <NumberFormatterSinSeparador value={totalSubTotalOperation} /> t CO<sub>2</sub>e{' '}
            </>
          )}
        </td>
      </tr>
    ),
  };

  return (
    <SummaryTabBase
      totalConstructionOverriden={totalConstructionOverriden}
      setTotalConstructionOverriden={setTotalConstructionOverriden}
      totalOperationOverriden={totalOperationOverriden}
      setTotalOperationOverriden={setTotalOperationOverriden}
      constructionTableConfig={constructionTableConfig}
      operationTableConfig={operationTableConfig}
      totalSubTotalConstruction={totalSubTotalConstruction}
      totalSubTotalOperation={totalSubTotalOperation}
      anyConstruction={anyConstruction}
      anyOperations={anyOperations}
      showNormalizedColumn={false}
    />
  );
};

export default SummaryTab;
